import { useSyncExternalStore } from 'react';
import { resolveRoute, type Route } from './route.ts';

/**
 * Reactive wrapper around `resolveRoute` (Task A). `useSyncExternalStore` treats
 * `window.location` as the external store: every subscriber recomputes its snapshot from
 * `location.pathname`/`location.search` on `popstate` — which fires for real back/forward
 * navigation, and which `navigate()` below dispatches synthetically after `pushState` (browsers
 * never fire `popstate` for `pushState` itself, so without this an in-app link would need its own
 * ad-hoc state plumbing instead of just calling `navigate()`).
 */

function subscribe(listener: () => void): () => void {
  window.addEventListener('popstate', listener);
  return () => window.removeEventListener('popstate', listener);
}

// Integration-pass fix: `useSyncExternalStore` compares snapshots with `Object.is`, and
// `resolveRoute()` returns a fresh object literal on every call — with no caching, calling
// `getSnapshot()` twice in a row for the SAME `location.pathname`/`location.search` produced two
// different references, which React reads as "the store changed" on every single render, which
// schedules another render, forever ("Maximum update depth exceeded" / "The result of getSnapshot
// should be cached", reproduced live the moment `App.tsx` first called `useRoute()`). Caching the
// last computed `Route` against the raw location string it came from keeps repeated calls between
// real navigations `Object.is`-equal, which is what `useSyncExternalStore` requires of a computed
// (non-primitive) snapshot.
let cachedKey: string | null = null;
let cachedRoute: Route | null = null;

function getSnapshot(): Route {
  const key = window.location.pathname + window.location.search;
  if (cachedRoute === null || cachedKey !== key) {
    cachedKey = key;
    cachedRoute = resolveRoute(window.location.pathname, window.location.search);
  }
  return cachedRoute;
}

// ponytail: no `getServerSnapshot` — `main.tsx` uses `createRoot`, never `hydrateRoot`, so this
// SPA has no server-rendered pass for one to reconcile against.

/** The current `Route`, re-rendering the caller on every navigation (back/forward or `navigate()`). */
export function useRoute(): Route {
  return useSyncExternalStore(subscribe, getSnapshot);
}

/** Pushes `path` onto history and notifies every `useRoute()` subscriber. `pushState` alone fires
 * no event (browsers only fire `popstate` for actual back/forward/`history.go`), so this dispatches
 * one synthetically right after — `dispatchEvent` invokes every listener `subscribe` registered
 * synchronously, in the same tick as this call. */
export function navigate(path: string): void {
  window.history.pushState(null, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}
